import { pageTitle } from '@palaccount/config';
import { type AccountingPeriodResponse } from '@palaccount/contracts';
import { Lock, LockOpen } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Ltr } from '@/components/amount';
import { ConfirmBox } from '@/components/confirm-box';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { AccountingTabs } from '@/features/accounts/accounting-tabs';
import { Modal } from '@/features/inventory/modal';
import { ApiError, localizeServerCause } from '@/lib/server-messages';
import { cn } from '@/lib/utils';

import { useClosePeriod, usePeriods, useReopenPeriod } from './api';

/**
 * ACCOUNTING PERIODS (Q-FIN-01-3) — the screen the glossary marked
 * "[NOT IN PROTOTYPE — decide at build time]" under `G-CLOSEDPERIOD`.
 *
 * It goes in Accounting & Finance because 16.2.2 row 6 puts "chart of
 * accounts, currencies, taxes, FISCAL PERIODS" in the `accounting` area, and
 * because Q-FIN-01-3 — the question this whole wave answers — is FIN-01's.
 *
 * The calendar is derived, so there is nothing to create: every month of
 * `fiscal.cycle_start`..`fiscal.cycle_end` is a row, and the only verbs are
 * Close and Reopen.
 *
 *   Close  — `accounting.approve`, the grant 16.5 gives posting a journal.
 *            GEN-93 refuses it while the month still holds drafts, and the
 *            refusal LISTS them, because "there are drafts" without saying
 *            which ones sends a person hunting.
 *   Reopen — the Owner, or a role holding `accounting.reopen_period`. It
 *            takes a typed reason and a confirmation that names the
 *            consequence, because it is the one action here that puts a
 *            reported month back into play (GEN-94).
 */
export function PeriodsPage() {
  const { t } = useTranslation();
  const { data, isPending } = usePeriods();
  const closePeriod = useClosePeriod();
  const reopenPeriod = useReopenPeriod();

  const [error, setError] = useState<string | null>(null);
  const [blockers, setBlockers] = useState<{ reference: string; date: string }[]>([]);
  const [reopening, setReopening] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  /**
   * The period awaiting its confirmation, or null. Holding the PERIOD rather
   * than a boolean is what lets the dialog name it — `periods.confirmClose`
   * always interpolated the code, and a boolean would have lost that.
   *
   * The old ask was `window.confirm`, which BR-PERM-15 (spec line 35168)
   * forbids: "a new pattern must not be invented", and browser chrome is
   * neither of §16.5.3's two patterns.
   *
   * NOT CITED AS GEN-209, DELIBERATELY. Closing a month is not one of that
   * rule's seven acts, and it is not irreversible either — `onReopen` sits
   * directly below in this file, and GEN-94 (already cited at it) is the rule
   * that keeps that door open. What is wrong here is the CHROME, not a missing
   * irreversibility gate, and citing GEN-209 would have been the wrong-rule
   * citation this project keeps catching.
   *
   * The first draft of this comment cited the FIN-05 rule family by its family
   * name alone, with no row number. That is not an identifier — the family's
   * real members all carry a two-digit suffix — and the coverage scanner reads
   * an identifier-shaped string in a comment as a citation, so it registered
   * as a dangling one. Caught by grepping `spec-rule-ids.txt` before
   * committing. Named in prose here rather than re-typed, because writing the
   * bad string even to warn about it is what minted the dangling citation the
   * second time.
   */
  const [closing, setClosing] = useState<AccountingPeriodResponse | null>(null);

  useEffect(() => {
    document.title = pageTitle(t('periods.title'));
  }, [t]);

  /**
   * Both verbs fail the same two ways, so they read the failure the same way.
   *
   * Before this the strip said `caught instanceof ApiError ? caught.message :
   * String(caught)`, which had two faults that look like one. A request that
   * never came back put the BROWSER's `TypeError: Failed to fetch` in the red
   * strip, in the browser's language, where a sentence belongs. And a refusal
   * that DID come back was shown verbatim in English — `PERIOD_MESSAGES`'
   * fixed sentences have had i18n keys waiting for them in
   * `STATIC_MESSAGE_KEYS` all along (`periods.errFutureDated`,
   * `periods.errReopenDenied`) and this screen never called the function that
   * reaches them, so an Arabic reader met both refusals in English.
   *
   * `localizeServerCause` fixes both, because both are the same mistake:
   * flattening the cause to a string discards the only thing that separates
   * "the server refused" from "the server never answered".
   *
   * The unanswered sentence is `periods.errSaveFailed` and NOT the roles
   * screen's key that carries the same words today — see the note on
   * `localizeServerCause`. Closing a month does not survive locally, so the
   * two sentences agree here; they are permitted to stop agreeing without a
   * caller having to be untangled from a shared string first.
   */
  const failed = (caught: unknown) => localizeServerCause(t, caught, 'periods.errSaveFailed');

  /** The click only OPENS the question (GEN-208); nothing closes yet. */
  const onClose = (period: AccountingPeriodResponse) => {
    setError(null);
    setBlockers([]);
    setClosing(period);
  };

  /** The dialog's verb — this is where the month actually closes. */
  const onCloseConfirmed = (period: AccountingPeriodResponse) => {
    setClosing(null);
    closePeriod.mutate(period.code, {
      onError: (caught) => {
        setError(failed(caught));
        // GEN-93's list travels on the refusal body, so it is shown at once
        // rather than behind a second request the person has to think of.
        const body = caught instanceof ApiError ? caught.body : undefined;
        const rows = (body as { blockers?: { reference: string; date: string }[] } | undefined)
          ?.blockers;
        setBlockers(rows ?? []);
      },
    });
  };

  const onReopen = (code: string) => {
    setError(null);
    setBlockers([]);
    reopenPeriod.mutate(
      { code, reason },
      {
        onSuccess: () => {
          setReopening(null);
          setReason('');
        },
        onError: (caught) => setError(failed(caught)),
      },
    );
  };

  return (
    <div className="grid gap-4">
      <AccountingTabs />

      <div className="flex flex-wrap items-baseline gap-3">
        <h2 className="me-auto text-lg font-bold">{t('periods.title')}</h2>
        {data ? (
          <p className="text-sm text-muted-foreground">
            {t('periods.cycle')} <Ltr value={data.cycleStart} className="font-mono" /> —{' '}
            <Ltr value={data.cycleEnd} className="font-mono" />
          </p>
        ) : null}
      </div>

      {/* The two owner switches decide whether any of this bites. A screen
          that hid that would be lying about what closing a month does. */}
      {data && !data.lockClosedPeriods ? (
        <p className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200">
          {t('periods.lockOff')}
        </p>
      ) : null}

      {error ? (
        <div className="rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-800 dark:border-red-900 dark:bg-red-950 dark:text-red-200">
          <p>{error}</p>
          {blockers.length > 0 ? (
            <ul className="mt-2 list-inside list-disc">
              {blockers.map((row) => (
                <li key={row.reference}>
                  <Ltr value={row.reference} className="font-mono" />{' '}
                  <Ltr value={row.date} className="font-mono text-xs" />
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('periods.colPeriod')}</TableHead>
                <TableHead>{t('periods.colRange')}</TableHead>
                <TableHead className="text-end">{t('periods.colPosted')}</TableHead>
                <TableHead className="text-end">{t('periods.colDrafts')}</TableHead>
                <TableHead>{t('periods.colStatus')}</TableHead>
                <TableHead>{t('periods.colWho')}</TableHead>
                <TableHead className="text-end">{t('periods.colActions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(data?.periods ?? []).map((period) => (
                <TableRow key={period.code} className={cn(period.isCurrent && 'bg-muted/40')}>
                  <TableCell>
                    <Ltr value={period.code} className="font-mono font-medium" />
                    {period.isCurrent ? (
                      <span className="ms-2 text-xs text-muted-foreground">
                        {t('periods.current')}
                      </span>
                    ) : null}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    <Ltr value={`${period.startsOn} → ${period.endsOn}`} className="font-mono text-xs" />
                  </TableCell>
                  <TableCell className="text-end">{period.postedEntries}</TableCell>
                  <TableCell className="text-end">{period.draftEntries}</TableCell>
                  <TableCell>
                    <span
                      className={cn(
                        'inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-semibold',
                        period.status === 'closed'
                          ? 'bg-muted text-muted-foreground'
                          : 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300',
                      )}
                    >
                      {period.status === 'closed' ? (
                        <Lock className="size-3" aria-hidden />
                      ) : (
                        <LockOpen className="size-3" aria-hidden />
                      )}
                      {t(`periods.status.${period.status}`)}
                    </span>
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {period.status === 'closed' && period.closedByName
                      ? t('periods.closedBy', {
                          who: period.closedByName,
                          when: period.closedAt?.slice(0, 10) ?? '',
                        })
                      : period.reopenedByName
                        ? t('periods.reopenedBy', {
                            who: period.reopenedByName,
                            when: period.reopenedAt?.slice(0, 10) ?? '',
                          })
                        : '—'}
                  </TableCell>
                  <TableCell className="text-end">
                    {period.status === 'open' && data?.canClose ? (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => onClose(period)}
                        disabled={closePeriod.isPending}
                      >
                        {t('periods.close')}
                      </Button>
                    ) : null}
                    {period.status === 'closed' && data?.canReopen ? (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setReopening(period.code);
                          setReason('');
                          setError(null);
                        }}
                      >
                        {t('periods.reopen')}
                      </Button>
                    ) : null}
                  </TableCell>
                </TableRow>
              ))}
              {!isPending && (data?.periods ?? []).length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="py-8 text-center text-muted-foreground">
                    {t('periods.empty')}
                  </TableCell>
                </TableRow>
              ) : null}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/*
        ADM-02 §8's shared box. `periods.confirmClose` used to carry the
        question and the consequence in one string — GEN-205's two slots run
        together — and it is now split at the question mark into
        `confirmCloseTitle` and `confirmCloseBody`.

        The verb is `periods.close`, the same words as the control that raised
        it, and never OK (GEN-206). No "cannot be undone" clause: the reopen
        panel is directly below.
      */}
      <ConfirmBox
        open={closing !== null}
        title={t('periods.confirmCloseTitle', { code: closing?.code ?? '' })}
        body={t('periods.confirmCloseBody')}
        confirmLabel={t('periods.close')}
        busy={closePeriod.isPending}
        onConfirm={() => {
          if (closing !== null) onCloseConfirmed(closing);
        }}
        onCancel={() => setClosing(null)}
      />

      {/* GEN-94's deliberate act: the consequence in words, a typed reason,
          and a second click. Never a toggle in the status column. */}
      {reopening !== null ? (
        // A pop-up, like the Close box beside it — as a card under the table
        // it opened out of sight (bugs: Accounting Periods).
        <Modal
          title={t('periods.reopenHeading', { code: reopening })}
          onClose={() => setReopening(null)}
          className="max-w-lg"
        >
          <div className="grid gap-3">
            <p className="text-sm text-muted-foreground">
              {/* PARAMETERISED — it names the period, so the exact-match map
                  has no single string to hold and no entry can ever exist for
                  it. Addressed as a dictionary key here, where the code is
                  known. `PERIOD_MESSAGES.reopenForbidden` on the same screen
                  IS fixed and does go through the map, via
                  `localizeServerCause` above; the two sentences take different
                  routes for a structural reason, not an inconsistent one. */}
              {t('periods.reopenConsequence', { code: reopening })}
            </p>
            <Label htmlFor="reopen-reason" className="text-xs">
              {t('periods.reasonLabel')}
            </Label>
            <Textarea
              id="reopen-reason"
              rows={2}
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              placeholder={t('periods.reasonPlaceholder')}
            />
            <div className="flex gap-2">
              <Button
                size="sm"
                onClick={() => onReopen(reopening)}
                disabled={reason.trim().length === 0 || reopenPeriod.isPending}
              >
                {t('periods.confirmReopen')}
              </Button>
              <Button size="sm" variant="outline" onClick={() => setReopening(null)}>
                {t('common.cancel')}
              </Button>
            </div>
          </div>
        </Modal>
      ) : null}
    </div>
  );
}
